import { createTheme } from "@mui/material/styles";
import materialTheme from "./material-theme.json";

// Module augmentation for custom palette key (Game of the Week)
declare module "@mui/material/styles" {
  interface Palette {
    gotw: Palette["primary"];
    scoring: Palette["primary"];
    lock: Palette["primary"];
    nail: Palette["primary"];
    pickerStats: Palette["primary"];
    toggle: Palette["primary"];
    surface: {
      lowest: string;
      low: string;
      container: string;
      high: string;
      highest: string;
    };
  }
  interface PaletteOptions {
    gotw?: PaletteOptions["primary"];
    scoring?: PaletteOptions["primary"];
    lock?: PaletteOptions["primary"];
    nail?: PaletteOptions["primary"];
    pickerStats?: PaletteOptions["primary"];
    toggle?: PaletteOptions["primary"];
    surface?: {
      lowest: string;
      low: string;
      container: string;
      high: string;
      highest: string;
    };
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    gotw: true;
  }
}

declare module "@mui/material/Switch" {
  interface SwitchPropsColorOverrides {
    toggle: true;
  }
}

declare module "@mui/material/Chip" {
  interface ChipPropsColorOverrides {
    gotw: true;
    scoring: true;
    lock: true;
    nail: true;
  }
}

// Extract Material Design 3 color schemes
const lightScheme = materialTheme.schemes.light;
const darkScheme = materialTheme.schemes.dark;

// Common theme configuration
const commonTransitions = {
  allProps: {
    duration: "0.3s",
    easing: "cubic-bezier(0.4, 0, 0.2, 1)",
  },
};

// Common component overrides
const createCommonComponents = (palette: any) => ({
  MuiCssBaseline: {
    styleOverrides: {
      html: {
        colorScheme: palette === darkScheme ? "dark" : "light",
        WebkitTextSizeAdjust: "100%",
      },
      body: {
        minWidth: 320,
        backgroundColor: palette.background,
        color: palette.onBackground,
      },
      "*:focus-visible": {
        outline: `3px solid ${palette.primary}`,
        outlineOffset: 2,
      },
      "@media (prefers-reduced-motion: reduce)": {
        "*, *::before, *::after": {
          animationDuration: "0.01ms !important",
          animationIterationCount: "1 !important",
          scrollBehavior: "auto !important",
          transitionDuration: "0.01ms !important",
        },
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        backgroundColor: palette.surface,
        color: palette.onSurface,
        boxShadow: `0 1px 3px ${palette.shadow}20`,
        transition: `all ${commonTransitions.allProps.duration} ${commonTransitions.allProps.easing}`,
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundColor: palette.surface,
        color: palette.onSurface,
        transition: `all ${commonTransitions.allProps.duration} ${commonTransitions.allProps.easing}`,
        "&.MuiPaper-elevation1": {
          backgroundColor: palette.surfaceContainer,
        },
        "&.MuiPaper-elevation2": {
          backgroundColor: palette.surfaceContainerHigh,
        },
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        backgroundColor: palette.surfaceContainer,
        color: palette.onSurface,
        transition: `all ${commonTransitions.allProps.duration} ${commonTransitions.allProps.easing}`,
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: "none" as const,
        fontWeight: 600,
        borderRadius: 20,
      },
      containedPrimary: {
        backgroundColor: palette.primary,
        color: palette.onPrimary,
        "&:hover": {
          backgroundColor: palette.primaryContainer,
          color: palette.onPrimaryContainer,
        },
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        minWidth: 44,
        minHeight: 44,
      },
    },
  },
  MuiFab: {
    styleOverrides: {
      root: {
        backgroundColor: palette.primary,
        color: palette.onPrimary,
        "&:hover": {
          backgroundColor: palette.primaryContainer,
          color: palette.onPrimaryContainer,
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        backgroundColor: palette.surfaceContainer,
        color: palette.onSurface,
        borderRadius: 12,
      },
    },
  },
});

export const lightTheme = createTheme({
  typography: {
    fontFamily: '"Roboto Variable", Roboto, Arial, sans-serif',
    h1: {
      fontSize: "2.25rem",
      lineHeight: 1.2,
      fontWeight: 700,
      letterSpacing: "-0.02em",
    },
    h2: {
      fontSize: "1.75rem",
      lineHeight: 1.25,
      fontWeight: 700,
      letterSpacing: "-0.015em",
    },
    h3: { fontSize: "1.5rem", lineHeight: 1.3, fontWeight: 700 },
    h4: { fontSize: "1.25rem", lineHeight: 1.35, fontWeight: 700 },
    h5: { fontSize: "1.125rem", lineHeight: 1.4, fontWeight: 700 },
    h6: { fontSize: "1rem", lineHeight: 1.4, fontWeight: 700 },
    button: { fontWeight: 700, textTransform: "none" },
  },
  palette: {
    mode: "light",
    primary: {
      main: lightScheme.primary,
      light: lightScheme.primaryContainer,
      dark: lightScheme.primaryFixedDim,
      contrastText: lightScheme.onPrimary,
    },
    secondary: {
      main: lightScheme.secondary,
      light: lightScheme.secondaryContainer,
      contrastText: lightScheme.onSecondary,
    },
    error: {
      main: "#E3524D",
      light: lightScheme.errorContainer,
      contrastText: "#190302",
    },
    warning: {
      main: lightScheme.error, // revert to design system or choose tertiary alt if distinct needed
      light: lightScheme.errorContainer,
      dark: lightScheme.error, // simplification
      contrastText: lightScheme.onError,
    },
    gotw: {
      main: "#F29C2E",
      light: "#FFECB3",
      dark: "#8A5200",
      contrastText: "#1a1a1a",
    },
    scoring: {
      main: "#30B07A",
      light: "#D8F5E7",
      dark: "#187A52",
      contrastText: "#071D14",
    },
    lock: {
      main: "#52A1F0",
      light: "#E1F0FF",
      dark: "#1768B8",
      contrastText: "#071727",
    },
    nail: {
      main: "#A37AE8",
      light: "#F0E9FC",
      dark: "#6742A7",
      contrastText: "#160A2B",
    },
    // Constant (non-adapting) color for picker stats text so it remains
    // visually consistent across light/dark modes. We intentionally reuse
    // same values in dark theme below.
    pickerStats: {
      main: "#37474F",
      light: "#546E7A",
      dark: "#263238",
      contrastText: "#FFFFFF",
    },
    toggle: {
      main: "#00897B",
      light: "#26A69A",
      dark: "#00695C",
      contrastText: "#FFFFFF",
    },
    surface: {
      lowest: "#FFFFFF",
      low: "#EFF5F0",
      container: "#EAEFEA",
      high: "#E4EAE4",
      highest: "#DEE4DF",
    },
    info: {
      main: lightScheme.tertiary,
      light: lightScheme.tertiaryContainer,
      contrastText: lightScheme.onTertiary,
    },
    success: {
      main: lightScheme.primary,
      light: lightScheme.primaryContainer,
      contrastText: lightScheme.onPrimary,
    },
    background: {
      default: lightScheme.background,
      paper: lightScheme.surface,
    },
    text: {
      primary: lightScheme.onSurface,
      secondary: lightScheme.onSurfaceVariant,
    },
    divider: lightScheme.outline,
    action: {
      hover: lightScheme.primary + "08",
      selected: lightScheme.primary + "12",
    },
  },
  transitions: {
    create: () =>
      `all ${commonTransitions.allProps.duration} ${commonTransitions.allProps.easing}`,
  },
  components: {
    ...createCommonComponents(lightScheme),
    MuiSwitch: {
      styleOverrides: {
        root: {
          padding: 8,
        },
        switchBase: {
          "&.Mui-checked": {
            color: "#00897B",
            "&:hover": {
              backgroundColor: "#00897B20",
            },
          },
        },
        track: {
          backgroundColor: "#B0BEC5",
          opacity: 1,
          ".Mui-checked + &": {
            backgroundColor: "#00897B",
            opacity: 1,
          },
        },
        thumb: {
          boxShadow: "0 1px 3px rgba(0,0,0,0.4)",
        },
      },
    },
  },
});

export const darkTheme = createTheme({
  typography: {
    fontFamily: '"Roboto Variable", Roboto, Arial, sans-serif',
    h1: {
      fontSize: "2.25rem",
      lineHeight: 1.2,
      fontWeight: 700,
      letterSpacing: "-0.02em",
    },
    h2: {
      fontSize: "1.75rem",
      lineHeight: 1.25,
      fontWeight: 700,
      letterSpacing: "-0.015em",
    },
    h3: { fontSize: "1.5rem", lineHeight: 1.3, fontWeight: 700 },
    h4: { fontSize: "1.25rem", lineHeight: 1.35, fontWeight: 700 },
    h5: { fontSize: "1.125rem", lineHeight: 1.4, fontWeight: 700 },
    h6: { fontSize: "1rem", lineHeight: 1.4, fontWeight: 700 },
    button: { fontWeight: 700, textTransform: "none" },
  },
  palette: {
    mode: "dark",
    primary: {
      main: darkScheme.primary,
      light: darkScheme.primaryContainer,
      dark: darkScheme.primaryFixedDim,
      contrastText: darkScheme.onPrimary,
    },
    secondary: {
      main: darkScheme.secondary,
      light: darkScheme.secondaryContainer,
      contrastText: darkScheme.onSecondary,
    },
    error: {
      main: "#E3524D",
      light: darkScheme.errorContainer,
      contrastText: "#190302",
    },
    warning: {
      main: darkScheme.error,
      light: darkScheme.errorContainer,
      dark: darkScheme.error,
      contrastText: darkScheme.onError,
    },
    gotw: {
      main: "#F29C2E",
      light: "#FFE082",
      dark: "#8A5200",
      contrastText: "#1a1a1a",
    },
    scoring: {
      main: "#30B07A",
      light: "#183C2D",
      dark: "#1D8C60",
      contrastText: "#071D14",
    },
    lock: {
      main: "#52A1F0",
      light: "#18334D",
      dark: "#2B7BCB",
      contrastText: "#071727",
    },
    nail: {
      main: "#A37AE8",
      light: "#36284F",
      dark: "#7D58C1",
      contrastText: "#160A2B",
    },
    pickerStats: {
      main: "#37474F",
      light: "#546E7A",
      dark: "#263238",
      contrastText: "#FFFFFF",
    },
    toggle: {
      main: "#00897B",
      light: "#26A69A",
      dark: "#00695C",
      contrastText: "#FFFFFF",
    },
    surface: {
      lowest: "#0A0F0D",
      low: "#171D1A",
      container: "#1B211E",
      high: "#252B28",
      highest: "#303633",
    },
    info: {
      main: darkScheme.tertiary,
      light: darkScheme.tertiaryContainer,
      contrastText: darkScheme.onTertiary,
    },
    success: {
      main: darkScheme.primary,
      light: darkScheme.primaryContainer,
      contrastText: darkScheme.onPrimary,
    },
    background: {
      default: darkScheme.background,
      paper: darkScheme.surface,
    },
    text: {
      primary: darkScheme.onSurface,
      secondary: darkScheme.onSurfaceVariant,
    },
    divider: darkScheme.outline,
    action: {
      hover: darkScheme.primary + "15",
      selected: darkScheme.primary + "20",
    },
  },
  transitions: {
    create: () =>
      `all ${commonTransitions.allProps.duration} ${commonTransitions.allProps.easing}`,
  },
  components: {
    ...createCommonComponents(darkScheme),
    MuiSwitch: {
      styleOverrides: {
        root: {
          padding: 8,
        },
        switchBase: {
          "&.Mui-checked": {
            color: "#26A69A",
            "&:hover": {
              backgroundColor: "#26A69A30",
            },
          },
        },
        track: {
          backgroundColor: "#455A64",
          opacity: 1,
          ".Mui-checked + &": {
            backgroundColor: "#26A69A",
            opacity: 1,
          },
        },
        thumb: {
          boxShadow: "0 1px 3px rgba(0,0,0,0.6)",
        },
      },
    },
  },
});
